import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Info } from 'lucide-react';

interface CertificateFormatBuilderProps {
  format: string;
  onFormatChange: (format: string) => void;
}

const getPlaceholders = (t: any) => [
  { key: '{YEAR}', label: t('certificate.formatBuilder.placeholders.year'), description: t('certificate.formatBuilder.placeholders.yearDescription') },
  { key: '{MONTH}', label: t('certificate.formatBuilder.placeholders.month'), description: t('certificate.formatBuilder.placeholders.monthDescription') },
  { key: '{DAY}', label: t('certificate.formatBuilder.placeholders.day'), description: t('certificate.formatBuilder.placeholders.dayDescription') },
  { key: '{PROCESS_ID}', label: t('certificate.formatBuilder.placeholders.processId'), description: t('certificate.formatBuilder.placeholders.processIdDescription') },
  { key: '{FORM_ID}', label: t('certificate.formatBuilder.placeholders.formId'), description: t('certificate.formatBuilder.placeholders.formIdDescription') },
  { key: '{SEQUENCE}', label: t('certificate.formatBuilder.placeholders.sequence'), description: t('certificate.formatBuilder.placeholders.sequenceDescription') },
  { key: '{UUID}', label: t('certificate.formatBuilder.placeholders.uuid'), description: t('certificate.formatBuilder.placeholders.uuidDescription') },
  { key: '{QUESTION_ID:xxx}', label: t('certificate.formatBuilder.placeholders.questionValue'), description: t('certificate.formatBuilder.placeholders.questionValueDescription') },
];

const CertificateFormatBuilder: React.FC<CertificateFormatBuilderProps> = ({
  format,
  onFormatChange,
}) => {
  const { t } = useTranslation();
  const [preview, setPreview] = useState('');

  const handlePlaceholderClick = (placeholder: string) => {
    const input = document.getElementById('format-input') as HTMLInputElement;
    if (input) {
      const start = input.selectionStart || 0;
      const end = input.selectionEnd || 0;
      const newFormat =
        format.substring(0, start) + placeholder + format.substring(end);
      onFormatChange(newFormat);
      // Set cursor position after inserted placeholder
      setTimeout(() => {
        input.focus();
        input.setSelectionRange(
          start + placeholder.length,
          start + placeholder.length,
        );
      }, 0);
    } else {
      onFormatChange(format + placeholder);
    }
  };

  const generatePreview = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');

    let previewText = format;
    previewText = previewText.replace(/{YEAR}/g, String(year));
    previewText = previewText.replace(/{MONTH}/g, month);
    previewText = previewText.replace(/{DAY}/g, day);
    previewText = previewText.replace(/{PROCESS_ID}/g, 'proc1234');
    previewText = previewText.replace(/{FORM_ID}/g, 'form5678');
    previewText = previewText.replace(/{SEQUENCE}/g, '0001');
    previewText = previewText.replace(/{UUID}/g, 'a1b2c3d4');
    previewText = previewText.replace(/{QUESTION_ID:[^}]+}/g, 'answer');

    setPreview(previewText);
  };

  React.useEffect(() => {
    generatePreview();
  }, [format]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('certificate.formatBuilder.title')}</CardTitle>
        <CardDescription>
          {t('certificate.formatBuilder.description')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('certificate.formatBuilder.formatString')}
          </label>
          <Input
            id="format-input"
            value={format}
            onChange={(e) => onFormatChange(e.target.value)}
            placeholder="CERT-{YEAR}-{SEQUENCE}"
            className="font-mono"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('certificate.formatBuilder.availablePlaceholders')}
          </label>
          <div className="grid grid-cols-2 gap-2">
            {getPlaceholders(t).map((placeholder) => (
              <Button
                key={placeholder.key}
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handlePlaceholderClick(placeholder.key)}
                className="justify-start text-left h-auto py-2"
              >
                <div>
                  <div className="font-mono text-xs font-semibold">
                    {placeholder.key}
                  </div>
                  <div className="text-xs text-gray-500">
                    {placeholder.label}
                  </div>
                </div>
              </Button>
            ))}
          </div>
        </div>

        <div className="p-4 bg-gray-50 rounded-lg">
          <div className="flex items-start gap-2">
            <Info className="h-5 w-5 text-blue-500 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900 mb-1">
                {t('certificate.formatBuilder.preview')}
              </p>
              <p className="font-mono text-sm text-gray-700 bg-white p-2 rounded border">
                {preview || format}
              </p>
            </div>
          </div>
        </div>

        <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
          <p className="text-xs text-blue-800">
            <strong>{t('certificate.formatBuilder.tip')}</strong> {t('certificate.formatBuilder.tipDescription')}
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default CertificateFormatBuilder;

